import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Status } from './../configs/status';
import { HttpService } from './http.service';

@Injectable({
  providedIn: 'root'
})
export class AdminService {

  basePath = '/admin';

  TRADE_LANE_PATH = `/trade-lanes`;
  LC_TRADE_LANE_PATH = `${this.basePath}${this.TRADE_LANE_PATH}`;
  USER_PATH = `${this.basePath}/users`;
  COMPANY_PATH = `${this.basePath}/companies`;
  ROLE_PATH = `${this.basePath}/roles`;
  ROLE_FEATURE_PATH = `${this.basePath}/roles/features`;
  USER = '/users';
  ROLE = '/roles';
  COMPANY = '/companies';
  FLOW_DEFAULT_PATH = `${this.basePath}/flows-default`;
  FLOW_DEFAULT_PERSONA = `/personas`;
  FLOW_DEFAULT_GROUP = `/groups`;
  COMPANY_INFO = '/companies/info';
  COMPANY_LOCATION = '/companies/locations';
  COMPANY_REGULATORY_FILING = '/companies-regulatory';
  COMPANY_REGULATORY_HISTORIES = '/companies-regulatory-histories';
  ADMIN_COMPANY_REGULATORY_FILING = `${this.COMPANY_PATH}-regulatory`;
  ADMIN_COMPANY_REGULATORY_HISTORIES = `${this.COMPANY_PATH}/:id/regulatory-histories`;
  PARTIES = '/parties';
  COMPANY_FIELDS = '/company/fields';
  COLLAR_TYPES = '/collar-types';
  COMPANY_LICENCE = `${this.COMPANY_PATH}/licence`;
  COMPANY_LICENCE_NAD = `${this.COMPANY}/licence`;
  COMPANY_LICENCES = `${this.COMPANY_PATH}/licences`;
  COMPANY_LICENCES_NAD = `${this.COMPANY}/licences`;

  constructor(private http: HttpService) { }

  getLocodes(params: object = {}): Observable<any> {
    return this.http.get(`/locodes`, params);
  }

  getPersonaAllowedActions(cargoType = 1): Observable<any> {
    return this.http.get(`/personas/actions/${cargoType}`);
  }

  // TRADE LANE MANAGEMENT
  getLCTradeLanes(prams: any): Observable<any> {
    return this.http.get(this.LC_TRADE_LANE_PATH, prams);
  }

  getTradeLanes(prams: any): Observable<any> {
    return this.http.get(`${this.TRADE_LANE_PATH}`, prams, {
      headers: { ignoreLoadingBar: '' },
    });
  }

  addLCTradeLane(body: object = {}): Observable<any> {
    return this.http.post(this.LC_TRADE_LANE_PATH, body);
  }

  addTradeLane(body: object = {}): Observable<any> {
    return this.http.post(this.TRADE_LANE_PATH, body);
  }

  getLCTradeLaneById(id: number): Observable<any> {
    return this.http.get(`${this.LC_TRADE_LANE_PATH}/${id}`);
  }

  copyLCTradeLaneById(id: number): Observable<any> {
    return this.http.post(`${this.LC_TRADE_LANE_PATH}/${id}/copy`, null);
  }

  getTradeLaneById(id: number): Observable<any> {
    return this.http.get(`${this.TRADE_LANE_PATH}/${id}`);
  }

  copyTradeLaneById(id: number): Observable<any> {
    return this.http.post(`${this.TRADE_LANE_PATH}/${id}/copy`, null);
  }

  editLCTradeLane(id: number, body: object = {}): Observable<any> {
    return this.http.patch(`${this.LC_TRADE_LANE_PATH}/${id}`, body);
  }

  editTradeLane(id: number, body: object = {}): Observable<any> {
    return this.http.patch(`${this.TRADE_LANE_PATH}/${id}`, body);
  }

  deleteLCTradeLane(id: number): Observable<any> {
    return this.http.delete(`${this.LC_TRADE_LANE_PATH}/${id}`);
  }

  deleteTradeLane(id: number): Observable<any> {
    return this.http.delete(`${this.TRADE_LANE_PATH}/${id}`);
  }

  updateTradeLaneFlow(id: number, body: object = {}): Observable<any> {
    return this.http.put(`${this.TRADE_LANE_PATH}/${id}/flow`, body);
  }

  updateLCTradeLaneFlow(id: number, body: object = {}): Observable<any> {
    return this.http.put(`${this.LC_TRADE_LANE_PATH}/${id}/flow`, body);
  }

  // USER MANAGEMENT
  getUsers(params: object = {}): Observable<any> {
    return this.http.get(`${this.USER_PATH}`, params);
  }

  getAdminUsers(params: object = {}): Observable<any> {
    return this.http.get(`${this.USER}`, params);
  }

  getUserById(id: number): Observable<any> {
    return this.http.get(`${this.USER_PATH}/${id}`);
  }

  getAdminUserById(id: string): Observable<any> {
    return this.http.get(`${this.USER}/${id}`);
  }

  addUser(body: object = {}): Observable<any> {
    return this.http.post(`${this.USER_PATH}`, body);
  }

  addAdminUser(body: object = {}): Observable<any> {
    return this.http.post(`${this.USER}`, body);
  }

  editUser(id: number, body: object = {}): Observable<any> {
    return this.http.patch(`${this.USER_PATH}/${id}`, body);
  }

  editAdminUser(id: string, body: object = {}): Observable<any> {
    return this.http.patch(`${this.USER}/${id}`, body);
  }

  deleteUser(id: number): Observable<any> {
    return this.http.delete(`${this.USER_PATH}/${id}`);
  }

  deleteAdminUser(id: string): Observable<any> {
    return this.http.delete(`${this.USER}/${id}`);
  }

  // COMPANY MANAGEMENT
  getCompanies(params: object = {}): Observable<any> {
    return this.http.get(`${this.COMPANY_PATH}`, params);
  }

  getCompaniesNotAdmin(params: object = {}): Observable<any> {
    return this.http.get(`${this.COMPANY}`, params);
  }

  getCompanyById(id: number): Observable<any> {
    return this.http.get(`${this.COMPANY_PATH}/${id}`);
  }

  getCompanyByIdentifier(identifier: string): Observable<any> {
    return this.http.get(`${this.COMPANY}/${identifier}`);
  }

  deleteCompanyById(id: string): Observable<any> {
    return this.http.delete(`${this.COMPANY_PATH}/${id}`);
  }

  editCompany(id: number, params: object = {}): Observable<any> {
    return this.http.patch(`${this.COMPANY_PATH}/${id}`, params);
  }

  inviteCompany(params: object = {}): Observable<any> {
    return this.http.post(`${this.COMPANY_PATH}`, params);
  }

  inviteCompanyNotAdmin(params: object = {}): Observable<any> {
    return this.http.post(`${this.COMPANY}`, params);
  }

  updateCompanyInfo(params: object = {}): Observable<any> {
    return this.http.patch(`${this.COMPANY_INFO}`, params);
  }

  updateCompanyLocation(params: object = {}): Observable<any> {
    return this.http.put(`${this.COMPANY_LOCATION}`, params);
  }

  deleteCompanyLocation(id: number): Observable<any> {
    return this.http.delete(`${this.COMPANY_LOCATION}/${id}`);
  }

  deleteCompany(): Observable<any> {
    return this.http.delete(`${this.COMPANY}`);
  }

  // ROLE MANAGEMENT

  getRoles(params: object = {}): Observable<any> {
    return this.http.get(`${this.ROLE_PATH}`, params);
  }

  getAdminRoles(params: object = {}): Observable<any> {
    return this.http.get(`${this.ROLE}`, params);
  }

  getRoleById(id: number): Observable<any> {
    return this.http.get(`${this.ROLE_PATH}/${id}`);
  }

  getRolesFeatures(params: object = {}): Observable<any> {
    return this.http.get(`${this.ROLE_FEATURE_PATH}`, params);
  }

  addRole(body: object = {}): Observable<any> {
    return this.http.post(`${this.ROLE_PATH}`, body);
  }

  editRole(id: number, params: object = {}): Observable<any> {
    return this.http.patch(`${this.ROLE_PATH}/${id}`, params);
  }

  deleteRole(id: number): Observable<any> {
    return this.http.delete(`${this.ROLE_PATH}/${id}`);
  }

  // DEFAULT FLOW MANAGEMENT
  getFlows(params: object = {}): Observable<any> {
    return this.http.get(`${this.FLOW_DEFAULT_PATH}`, params);
  }

  getFlowById(id, params): Observable<any> {
    return this.http.get(`${this.FLOW_DEFAULT_PATH}/${id}`, params);
  }

  upsertFlowInfo(body: object = {}): Observable<any> {
    return this.http.put(`${this.FLOW_DEFAULT_PATH}`, body);
  }

  upsertJourneyPoint(body: object = {}, id): Observable<any> {
    return this.http.put(`${this.FLOW_DEFAULT_PATH}/${id}/steps`, body);
  }


  deleteFlow(id: number): Observable<any> {
    return this.http.delete(`${this.FLOW_DEFAULT_PATH}/${id}`);
  }

  activeFlow(flowId: number): Observable<any> {
    return this.http.put(`${this.FLOW_DEFAULT_PATH}`, {
      id: flowId,
      status: Status.FlowStatus.Active,
    });
  }

  inactiveFlow(flowId: number): Observable<any> {
    return this.http.put(`${this.FLOW_DEFAULT_PATH}`, {
      id: flowId,
      status: Status.FlowStatus.Inactive,
    });
  }

  updateFlowPersona(id: number, body: object = {}): Observable<any> {
    return this.http.put(`${this.FLOW_DEFAULT_PATH}/${id}${this.FLOW_DEFAULT_PERSONA}`, body);
  }

  updateFlowGroup(id: number, body: object = {}): Observable<any> {
    return this.http.put(`${this.FLOW_DEFAULT_PATH}/${id}${this.FLOW_DEFAULT_GROUP}`, body);
  }

  upsertActions(id, body: object = {}, stepId): Observable<any> {
    return this.http.put(`${this.FLOW_DEFAULT_PATH}/${id}/steps/${stepId}`, body);
  }

  // REGULATORY FILING
  getRegulatoryFiling(params): Observable<any> {
    return this.http.get(`${this.COMPANY_REGULATORY_FILING}`, params);
  }

  putRegulatoryFiling(params): Observable<any> {
    return this.http.put(`${this.COMPANY_REGULATORY_FILING}`, params);
  }
  getRegulatoryFilingHistories(params): Observable<any> {
    return this.http.get(`${this.COMPANY_REGULATORY_HISTORIES}`, params);
  }

  getAdminRegulatory(params: object = {}): Observable<any> {
    return this.http.get(`${this.ADMIN_COMPANY_REGULATORY_FILING}`, params);
  }
  getAdminRegulatoryByID(id): Observable<any> {
    return this.http.get(`${this.ADMIN_COMPANY_REGULATORY_FILING}/${id}`);
  }
  patchAdminRegulatoryByID(id, params): Observable<any> {
    return this.http.patch(`${this.ADMIN_COMPANY_REGULATORY_FILING}/${id}`, params);
  }
  getAdminRegulatoryHistoriesByID(id, params): Observable<any> {
    return this.http.get(`${this.ADMIN_COMPANY_REGULATORY_HISTORIES}`.replace(':id', id), params);
  }

  getParties(params: object = {}): Observable<any> {
    return this.http.get(`${this.PARTIES}`, params);
  }

  createParty(body: object = {}): Observable<any> {
    return this.http.post(`${this.PARTIES}`, body);
  }

  deleteParty(id: number): Observable<any> {
    return this.http.delete(`${this.PARTIES}/${id}`);
  }

  getCompanyFields(params: object = {}): Observable<any> {
    return this.http.get(`${this.COMPANY_FIELDS}`, params);
  }

  getCollarTypes(params: object = {}): Observable<any> {
    return this.http.get(`${this.COLLAR_TYPES}`, params);
  }

  postCompanyLicence(body: object = {}): Observable<any> {
    return this.http.post(`${this.COMPANY_LICENCE}`, body);
  }

  postCompanyLicences(body: object = {}): Observable<any> {
    return this.http.post(`${this.COMPANY_LICENCES_NAD}`, body);
  }

  deleteCompanyLicence(id: number): Observable<any> {
    return this.http.delete(`${this.COMPANY_LICENCE_NAD}/${id}`);
  }
}
